import { FC, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Button } from 'react-daisyui';
import Routes from '@/router/Routes.ts';
import useAdminFamilies from '@/api/useAdminFamilies.ts';
import useAdminFamilyOnboarding from '@/api/useAdminFamilyOnboarding.ts';
import useAdminDeleteUser from '@/api/useAdminDeleteUser.ts';
import DeleteUserModal from '@/components/modals/DeleteUserModal.tsx';
import { formatCareType } from '@/utils/formatCareType.ts';

const AdminFamilyDetailsPage: FC = () => {
  const { familyId } = useParams<{ familyId: string }>();
  const navigate = useNavigate();
  const { data: families, isLoading, isError } = useAdminFamilies();
  const { data: onboarding, isLoading: isLoadingOnboarding } = useAdminFamilyOnboarding(familyId);
  const { mutate: deleteUser, isPending: isDeleting, isError: isDeleteError, error: deleteError } = useAdminDeleteUser();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const family = families?.find((f) => f.id === familyId);

  const handleDelete = () => {
    if (!familyId) return;
    deleteUser(familyId, {
      onSuccess: () => {
        setIsDeleteModalOpen(false);
        navigate(Routes.ADMIN_FAMILIES);
      },
    });
  };

  if (isLoading || isLoadingOnboarding) {
    return (
      <div className="flex flex-col gap-6 w-full">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-lg mb-4" />
          <div className="h-64 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  if (isError || !family) {
    return (
      <div className="flex flex-col gap-6 w-full">
        <button 
          onClick={() => navigate(Routes.ADMIN_FAMILIES)} 
          className="w-10 h-10 rounded-[12px] bg-black flex items-center justify-center text-white hover:opacity-80 cursor-pointer"
        >
          <img src="/assets/arrowLeft.svg" alt="back" style={{ width: '16.5px', height: '15px' }} />
        </button>
        <div className="text-red-500">Family member not found</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex items-center justify-between">
        <button 
          onClick={() => navigate(Routes.ADMIN_FAMILIES)} 
          className="w-10 h-10 rounded-[12px] bg-black flex items-center justify-center text-white hover:opacity-80 cursor-pointer"
        >
          <img src="/assets/arrowLeft.svg" alt="back" style={{ width: '16.5px', height: '15px' }} />
        </button>
        <Button
          onClick={() => setIsDeleteModalOpen(true)}
          className="bg-white text-[#FF5861] border border-[#FF5861] hover:bg-[#FF5861] hover:text-white rounded-[12px] px-6"
        >
          Delete
        </Button>
      </div>

      <div className="bg-[#F6F6F4] rounded-xl p-4 sm:p-6 space-y-6">
        <div className="rounded-lg">
          <label className="text-sm text-[#9CA3AF] flex flex-col">Name</label>
          <div className="bg-white rounded-[120px] mt-2 px-4 py-2 inline-block">
            <p className="text-sm font-bold text-black">
              {family.first_name || family.last_name
                ? `${family.first_name ?? ''} ${family.last_name ?? ''}`.trim()
                : family.email.split('@')[0]}
            </p>
          </div>
        </div>

        <div className="bg-[#F6F6F4] rounded-lg">
          <label className="text-sm text-[#9CA3AF] block mb-1">Email</label>
          <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block max-w-full break-all">
            {family.email}
          </p>
        </div>

        {family.phone_number && (
          <div className="bg-[#F6F6F4] rounded-lg">
            <label className="text-sm text-[#9CA3AF] block mb-1">Phone Number</label>
            <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
              {family.phone_number}
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-4">
          {family.gender && (
            <div className="bg-[#F6F6F4] rounded-lg">
              <label className="text-sm text-[#9CA3AF] block mb-1">Gender</label>
              <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
                {family.gender}
              </p>
            </div>
          )}
          {family.city_town && (
            <div className="bg-[#F6F6F4] rounded-lg">
              <label className="text-sm text-[#9CA3AF] block mb-1">City / Town</label>
              <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
                {family.city_town}
              </p>
            </div>
          )}
          {family.postcode && (
            <div className="bg-[#F6F6F4] rounded-lg">
              <label className="text-sm text-[#9CA3AF] block mb-1">Postcode</label>
              <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
                {family.postcode}
              </p>
            </div>
          )}
        </div>

        <div className="bg-[#F6F6F4] rounded-lg">
          <label className="text-sm text-[#9CA3AF] block mb-1">Account Created</label>
          <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
            {dayjs(family.created_at).format('ddd, DD MMM YYYY — HH:mm')}
          </p>
        </div>

        {onboarding && (
          <>
            <div className="bg-[#F6F6F4] rounded-lg">
              <label className="text-sm text-[#9CA3AF] block mb-1">Who Needs Care</label>
              <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block capitalize">
                {onboarding.who_needs_care}
              </p>
            </div>

            <div className="bg-[#F6F6F4] rounded-lg">
              <label className="text-sm text-[#9CA3AF] block mb-1">Care Type</label>
              <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
                {formatCareType(onboarding.care_type)}
              </p>
            </div>

            <div className="bg-[#F6F6F4] rounded-lg">
              <label className="text-sm text-[#9CA3AF] block mb-1">Frequency</label>
              <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block capitalize">
                {onboarding.frequency}
              </p>
            </div>

            <div className="bg-[#F6F6F4] rounded-lg">
              <label className="text-sm text-[#9CA3AF] block mb-1">Urgency</label>
              <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block capitalize">
                {onboarding.urgency}
              </p>
            </div>

            <div className="bg-[#F6F6F4] rounded-lg">
              <label className="text-sm text-[#9CA3AF] block mb-1">Start Type</label>
              <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block capitalize">
                {onboarding.start_type}
              </p>
            </div>

            {onboarding.start_date && (
              <div className="bg-[#F6F6F4] rounded-lg">
                <label className="text-sm text-[#9CA3AF] block mb-1">Start Date</label>
                <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
                  {dayjs(onboarding.start_date).format('ddd, DD MMM YYYY')}
                </p>
              </div>
            )}

            <div className="bg-[#F6F6F4] rounded-lg">
              <label className="text-sm text-[#9CA3AF] block mb-1">Location</label>
              <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
                {onboarding.location}
              </p>
            </div>

            {Number(onboarding.hourly_budget) > 0 && (
              <div className="bg-[#F6F6F4] rounded-lg">
                <label className="text-sm text-[#9CA3AF] block mb-1">Hourly Budget</label>
                <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
                  £{onboarding.hourly_budget}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <DeleteUserModal
        isOpen={isDeleteModalOpen}
        onClose={() => isDeleting ? null : setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        userName={family.email}
        userType="family member"
        isLoading={isDeleting}
        error={isDeleteError ? (deleteError as Error)?.message : null}
      />
    </div>
  );
};

export default AdminFamilyDetailsPage;
